/* eslint-disable react/display-name */
import React from 'react';
import ToolTip from '@atlaskit/tooltip';
import EditIcon from '@atlaskit/icon/glyph/edit';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import AddIcon from '@atlaskit/icon/glyph/add';
import ArrowLeftIcon from '@atlaskit/icon/glyph/arrow-left';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Button, { ButtonProps } from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { css } from '@emotion/core';
import { useQuery } from '@tanstack/react-query';

import { Alert } from '@/components/composed/modals';
import {
  DeleteEntityContext,
  EditContext,
  useErrorController,
  useFetchErrorController,
  useModalController,
} from '@/controllers';
import { CRUDableType } from '@/meta';
import { apiFetch } from '@/services';
import { querySpec } from '@/services/api-service/api-queries';

export const MoreButton = React.forwardRef<HTMLElement, ButtonProps>((props, ref) => (
  <Button
    ref={ref}
    appearance="subtle"
    iconBefore={
      <ToolTip position="top" content="More">
        <MoreIcon size="small" label="More" />
      </ToolTip>
    }
    testId="more"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
));
export const AddButton = React.forwardRef<HTMLElement, ButtonProps>((props, ref) => (
  <Button
    ref={ref}
    appearance="default"
    iconBefore={
      <ToolTip position="top" content="Create">
        <AddIcon size="small" label="Add" />
      </ToolTip>
    }
    testId="add"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
));
export const BackButton = React.forwardRef<HTMLElement, ButtonProps>((props, ref) => (
  <Button
    ref={ref}
    appearance="subtle"
    iconBefore={
      <ToolTip position="top" content="Back">
        <ArrowLeftIcon size="large" label="Back" />
      </ToolTip>
    }
    testId="back"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
));
export const CopyButton = React.forwardRef<HTMLElement, ButtonProps>((props, ref) => (
  <Button
    ref={ref}
    appearance="subtle"
    iconBefore={
      <ToolTip position="top" content="Copy ID">
        <CopyIcon size="small" label="Copy" />
      </ToolTip>
    }
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
));
export const EditButton = React.forwardRef<HTMLElement, ButtonProps>((props, ref) => (
  <Button
    ref={ref}
    appearance="subtle"
    iconBefore={
      <ToolTip position="top" content="Edit">
        <EditIcon size="small" label="Edit" />
      </ToolTip>
    }
    testId="edit"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
));
export const DeleteButton = React.forwardRef<HTMLElement, ButtonProps>((props, ref) => (
  <Button
    ref={ref}
    appearance="subtle"
    iconBefore={
      <ToolTip position="top" content="Delete">
        <TrashIcon size="small" label="Delete" />
      </ToolTip>
    }
    testId="delete"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
));
export const ViewButton = React.forwardRef<HTMLElement, ButtonProps>((props, ref) => (
  <Button
    ref={ref}
    appearance="subtle"
    iconBefore={
      <ToolTip position="top" content="Inspect">
        <WatchIcon size="small" label="Inspect" />
      </ToolTip>
    }
    testId="view"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
));

/* Returns a callback for a handler to generate a temporary SignalFX chart via API call and
   open in a new browser tab or window */
const useCreateTempChart = (queryKey: string[], path: string) => {
  const { onError } = useFetchErrorController();
  const { data, refetch } = useQuery(queryKey, () => apiFetch(path), {
    onError,
    enabled: false,
  });

  const onCreateTempChart = React.useCallback(() => {
    const openTab = (url: string) => !!url && window.open(url, '_blank')?.focus();

    if (data) {
      openTab(data?.url);
    } else {
      refetch().then(result => {
        openTab(result.data?.url);
      });
    }
  }, [refetch, data]);

  return { onCreateTempChart };
};

export function useCreateTempErrorBudgetGraphAction(publicId: string) {
  const { queryKey, path } = querySpec('slo.{id}.signalfx.chartWithErrorBudget', { id: publicId });
  const { onCreateTempChart: onCreateTempErrorBudgetChart } = useCreateTempChart(queryKey, path);
  return { onCreateTempErrorBudgetChart };
}

export function useCreateTempBurnRateGraphAction(publicId: string) {
  const { queryKey, path } = querySpec('slo.{id}.signalfx.chartWithBurnRates', { id: publicId });
  const { onCreateTempChart: onCreateTempBurnRateChart } = useCreateTempChart(queryKey, path);
  return { onCreateTempBurnRateChart };
}

export function useActions(
  publicId: string,
  entityName: string | undefined,
  type: CRUDableType,
  onDeleteSuccess: () => void | undefined = () => {}
) {
  const { dispatch: dispatchEdit, onOpen } = React.useContext(EditContext);
  const { dispatch: dispatchDelete } = React.useContext(DeleteEntityContext);
  const { onOpen: modalOnOpen } = useModalController();
  const setGlobalError = useErrorController();

  const onCopySfxQuery = React.useCallback(
    (goodQuery: string = '', badQuery: string = '', eventsQuery: string = '') => {
      /*
      NOTE:
      The below programText format is sensitive to tabs when pasted into SignalFx
      */
      const programText = `${goodQuery}\n${badQuery}\n${eventsQuery}`.trim();
      navigator.clipboard.writeText(programText).catch(reason => {
        setGlobalError({
          title: 'Could not copy SignalFlow to clipboard',
          message: `Could not copy the good and bad query SignalFlow of this SLO to the clipboard.${reason ??
            ''}`,
        });
      });
    },
    [setGlobalError]
  );

  const onCopy = React.useCallback(() => {
    navigator.clipboard
      .writeText(publicId)
      .then(async () => {
        await Alert({
          title: 'Info',
          body: (
            <>
              Successfully copied <b>{publicId}</b> to the clipboard
            </>
          ),
        });
      })
      .catch(reason => {
        setGlobalError({
          title: 'Could not copy to clipboard',
          message: `Could not copy the id of this entity to the clipboard.${reason ?? ''}`,
        });
      });
  }, [publicId, setGlobalError]);

  const onEdit = React.useCallback(() => {
    dispatchEdit({ publicId, type });
    onOpen();
  }, [dispatchEdit, publicId, type, onOpen]);

  const onDelete = React.useCallback(() => {
    dispatchDelete({ publicId, type, entityName, onDeleteSuccess });
    modalOnOpen();
  }, [dispatchDelete, entityName, modalOnOpen, onDeleteSuccess, publicId, type]);

  return { onCopySfxQuery, onCopy, onEdit, onDelete };
}

interface ActionButtons {
  id: string;
  entityName: string | undefined;
  type: CRUDableType;
  hideOnBlur?: boolean;
}

const ActionButtons: React.FC<ActionButtons> = ({ id, entityName, type, hideOnBlur = false }) => {
  const { onCopy, onEdit, onDelete } = useActions(id, entityName, type);
  return (
    <DropdownMenu
      testId={`${type}-more-actions`}
      trigger={({ triggerRef, ...props }) => (
        <MoreButton
          ref={triggerRef}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          css={
            hideOnBlur &&
            css`
              visibility: hidden;
              .capability:hover & {
                visibility: unset;
              }
            `
          }
        />
      )}
    >
      <DropdownItemGroup>
        <DropdownItem
          elemBefore={<CopyIcon size="small" label="Copy" testId={`${type}-more-actions-copyid`} />}
          onClick={onCopy}
        >
          Copy ID
        </DropdownItem>
        <DropdownItem
          elemBefore={<EditIcon size="small" label="Edit" testId={`${type}-more-actions-edit`} />}
          onClick={onEdit}
        >
          Edit
        </DropdownItem>
        <DropdownItem
          elemBefore={
            <TrashIcon size="small" label="Delete" testId={`${type}-more-actions-delete`} />
          }
          onClick={onDelete}
        >
          Delete
        </DropdownItem>
      </DropdownItemGroup>
    </DropdownMenu>
  );
};

export default ActionButtons;
